import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
    modelAliasAPI, offersAPI, ownProductsAPI, alertsAPI, mappingAPI, priceDynamicsAPI,
} from '../services/api';
import { Icon, LogoMark } from '../ui';
import './AppLayout.css';

// Каркас застосунку: бокове меню на комп'ютері, нижні вкладки й меню «Ще»
// на телефоні. Пункти меню залежать від прав користувача.
const useNavGroups = () => {
    const { user, canAccessDatabase, canScrape, isAdmin } = useAuth();
    if (!user) return [];
    const db = canAccessDatabase();
    return [
        {
            label: 'Аналітика',
            items: [
                { to: '/dashboard', label: 'Огляд', icon: 'overview', tab: true },
                db && { to: '/price-dynamics', label: 'Динаміка цін', icon: 'dynamics', tab: true },
                db && { to: '/comparison', label: 'Порівняння цін', icon: 'compare' },
            ],
        },
        {
            label: 'Каталоги',
            items: [
                db && { to: '/products', label: 'Мій каталог', icon: 'catalog', tab: true, short: 'Каталог' },
                db && { to: '/competitors', label: 'Конкуренти', icon: 'competitors', tab: true },
                db && { to: '/mapping', label: 'Зіставлення', icon: 'mapping', badge: 'review' },
            ],
        },
        {
            label: 'Сервіс',
            items: [
                canScrape() && { to: '/', label: 'Парсер', icon: 'parser', end: true },
                isAdmin() && { to: '/admin', label: 'Адміністрування', icon: 'admin' },
            ],
        },
    ]
        .map(g => ({ ...g, items: g.items.filter(Boolean) }))
        .filter(g => g.items.length);
};

// Попереднє завантаження даних сторінки, поки курсор над пунктом меню:
// між наведенням і кліком минає 200–400 мс, і за цей час відповіді вже в
// кеші (services/api.js). Параметри — рівно ті, з якими сторінка питає дані
// при відкритті, інакше кеш не спрацює.
const quiet = (...promises) => promises.forEach(p => p && p.catch(() => {}));
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toLocaleDateString('sv-SE'); };
const PREFETCH = {
    '/dashboard': () => quiet(
        offersAPI.dashboard(), offersAPI.facets(), alertsAPI.list({ hours: 72, limit: 8 }),
        offersAPI.list({ limit: 25, offset: 0, has_price: true })),
    '/products': () => quiet(
        ownProductsAPI.summary(), ownProductsAPI.list('', 50, 0, ''), offersAPI.sources()),
    '/competitors': () => quiet(offersAPI.dashboard()),
    '/price-dynamics': () => quiet(
        offersAPI.sources(), priceDynamicsAPI.range('infoshina'),
        priceDynamicsAPI.summary('infoshina', [daysAgo(7), daysAgo(1), daysAgo(0)], true)),
    '/mapping': () => quiet(mappingAPI.listAll(), modelAliasAPI.list('review'), offersAPI.sources()),
};
const prefetch = (to) => PREFETCH[to] && PREFETCH[to]();

const ROLE_LABELS = { admin: 'Адміністратор', user: 'Користувач', guest: 'Гість' };
const STATUS_LABELS = { pending: 'очікує схвалення', rejected: 'доступ відхилено' };

const AppLayout = ({ children }) => {
    const { user, logout, canAccessDatabase } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const groups = useNavGroups();
    const [reviewCount, setReviewCount] = useState(0);
    const [moreOpen, setMoreOpen] = useState(false);
    const canDb = !!user && canAccessDatabase();

    // Скільки пар моделей чекає рішення — лічильник біля «Зіставлення».
    // Перечитується при переході між сторінками: після розбору черги число
    // має зменшитись без перезавантаження.
    useEffect(() => {
        if (!canDb) return;
        let alive = true;
        modelAliasAPI.list('review', { limit: 1 })
            .then(d => { if (alive) setReviewCount(d?.counts?.review || 0); })
            .catch(() => {});
        return () => { alive = false; };
    }, [canDb, location.pathname]);

    useEffect(() => { setMoreOpen(false); }, [location.pathname]);

    // Найчастіший наступний крок після «Огляду» — каталог: гріємо його кеш,
    // коли браузер вільний, щоб перший перехід теж був миттєвим.
    useEffect(() => {
        if (!canDb) return undefined;
        const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1500));
        const cancel = window.cancelIdleCallback || clearTimeout;
        const handle = idle(() => prefetch('/products'));
        return () => cancel(handle);
    }, [canDb]);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const badgeFor = (item) => (item.badge === 'review' && reviewCount > 0 ? reviewCount : null);
    const allItems = groups.flatMap(g => g.items);
    const tabs = allItems.filter(i => i.tab).slice(0, 4);
    const initial = (user?.email || '?').charAt(0).toUpperCase();
    const themeLabel = theme === 'dark' ? 'Увімкнути світлу тему' : 'Увімкнути темну тему';

    const navLink = (item) => (
        <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => `app-nav-item${isActive ? ' is-active' : ''}`}
            onMouseEnter={() => prefetch(item.to)}
            onFocus={() => prefetch(item.to)}
        >
            <Icon name={item.icon} />
            <span className="app-nav-label">{item.label}</span>
            {badgeFor(item) && <span className="app-nav-badge">{badgeFor(item) > 99 ? '99+' : badgeFor(item)}</span>}
        </NavLink>
    );

    const userCard = user && (
        <div className="app-user">
            <span className="app-user-avatar">{initial}</span>
            <div className="app-user-text">
                <div className="ellipsis app-user-email" title={user.email}>{user.email}</div>
                <div className="app-user-role">
                    {ROLE_LABELS[user.role] || user.role}
                    {STATUS_LABELS[user.status] && <span className="app-user-status"> · {STATUS_LABELS[user.status]}</span>}
                </div>
            </div>
            <button className="ui-icon-btn ui-icon-btn--plain" onClick={toggleTheme} aria-label={themeLabel} title={themeLabel}>
                <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
            </button>
            <button className="ui-icon-btn ui-icon-btn--plain" onClick={handleLogout} aria-label="Вийти" title="Вийти">
                <Icon name="logout" />
            </button>
        </div>
    );

    return (
        <div className="app-shell">
            <aside className="app-side">
                <Link to="/dashboard" className="app-brand">
                    <LogoMark />
                    <span>
                        <span className="app-brand-name">Rezina</span>
                        <span className="app-brand-sub">моніторинг цін</span>
                    </span>
                </Link>

                <nav aria-label="Розділи" className="app-nav">
                    {groups.map(g => (
                        <div key={g.label} className="app-nav-group">
                            <div className="app-nav-group-label">{g.label}</div>
                            {g.items.map(navLink)}
                        </div>
                    ))}
                </nav>

                <div style={{ flex: 1 }} />
                {userCard}
            </aside>

            <header className="app-topbar">
                <Link to="/dashboard" className="app-brand">
                    <LogoMark size={34} />
                    <span className="app-brand-name">Rezina</span>
                </Link>
                <button className="ui-icon-btn" onClick={toggleTheme} aria-label={themeLabel}>
                    <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
                </button>
            </header>

            <main className="app-main">{children}</main>

            {user && (
                <nav className="app-tabs" aria-label="Розділи">
                    {tabs.map(item => (
                        <NavLink key={item.to} to={item.to} end={item.end}
                                 onTouchStart={() => prefetch(item.to)}
                                 className={({ isActive }) => `app-tab${isActive ? ' is-active' : ''}`}>
                            <Icon name={item.icon} size={24} />
                            <span>{item.short || item.label}</span>
                        </NavLink>
                    ))}
                    <button className={`app-tab${moreOpen ? ' is-active' : ''}`} onClick={() => setMoreOpen(o => !o)}
                            aria-expanded={moreOpen}>
                        <span style={{ position: 'relative', display: 'inline-flex' }}>
                            <Icon name="menu" size={24} />
                            {reviewCount > 0 && <span className="app-tab-dot" />}
                        </span>
                        <span>Ще</span>
                    </button>
                </nav>
            )}

            {moreOpen && (
                <div className="app-more-overlay" onClick={() => setMoreOpen(false)}>
                    <div className="app-more" onClick={e => e.stopPropagation()}>
                        {groups.map(g => (
                            <div key={g.label} className="app-nav-group">
                                <div className="app-nav-group-label">{g.label}</div>
                                {g.items.map(navLink)}
                            </div>
                        ))}
                        {userCard}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AppLayout;
